import { useCallback, useRef, useState } from "react";

/**
 * Tracks an in-flight async action so the UI can block interaction while it
 * runs. `run(fn)` executes fn, flips `busy` true for its duration, and ignores
 * re-entrant calls — a second trigger while one is in flight is a no-op, so a
 * mutation (add task, delete task, …) can't be fired twice. Returns fn's
 * result and re-throws its errors, so callers/awaiters keep working unchanged.
 *
 * Pair with <BusyOverlay show={busy} /> to block pointer events page-wide while
 * the action runs:
 *
 *   const { busy, run } = useBusy();
 *   const onDelete = (id) => run(async () => { await api.delete(...); load(); });
 *   ...
 *   <BusyOverlay show={busy} label="Working…" />
 */
export function useBusy() {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const run = useCallback(async (fn) => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    setBusy(true);
    try {
      return await fn();
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, []);

  return { busy, run };
}
